/**
 * @fileoverview HumioIntegrationProvider manages Humio/LogScale API session tokens and dashboard data.
 *
 * This provider handles:
 * - API token lifecycle management with automatic refresh before expiry
 * - Dashboard data fetching with automatic retry on authentication failures
 * - Terms acceptance flow for EULA and Humio terms
 *
 * Token Management Strategy:
 * - API tokens expire after 24 hours (Humio backend setting)
 * - Proactive refresh at 22 hours to prevent expiration (2-hour buffer for clock skew)
 * - Token age determined by lastUserActivityTimestamp (Humio's tracking) when available
 * - Falls back to session_id_token_created_at if dashboard never visited
 * - Automatic retry with exponential backoff on failures
 *
 * @module HumioIntegrationProvider
 */

import { ApolloError } from '@apollo/client';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useGetHumioDashboards } from '../../../features/investigations/queries';
import { setHumioSessionId } from '../../../features/investigations/utils';
import {
  useAcceptEulaTC,
  useAcceptHumioTC,
} from '../../../features/user/queries';
import { getIdTokenSafely } from '../../constants';
import { useTenantInfo } from '../../hooks';
import { getUserId } from '../../providers';
import { AuthContext } from '../AuthProvider/AuthContext';
import { eventBus, getUserScopedKey, isValidToken } from '../shared/utils';

import { INITIAL_STATE, TOKEN_CONFIG } from './HumioIntegrationProvider.const';
import { HumioIntegrationContext } from './HumioIntegrationProvider.context';
import {
  AcceptHumioResponse,
  AcceptTermsOptions,
  AcceptTermsResult,
  DashboardRefetchFunction,
  HumioIntegrationProviderProps,
  HumioIntegrationState,
} from './HumioIntegrationProvider.types';
import {
  calculateRetryDelay,
  checkRateLimit,
  clearAllCaches,
  clearTokenFromCache,
  error,
  getCachedToken,
  hasStorage,
  isAuthenticationError,
  isDownstreamError,
  needsTokenRefresh,
  recordRefreshAttempt,
  refreshAttempts,
  shouldThrottleRefresh,
  updateTokenCache,
  warn,
} from './HumioIntegrationProvider.utils';

/**
 * Provider component that manages Humio/LogScale integration
 *
 * @component
 * @example
 * ```tsx
 * <HumioIntegrationProvider>
 *   <App />
 * </HumioIntegrationProvider>
 * ```
 */
export const HumioIntegrationProvider: React.FC<
  HumioIntegrationProviderProps
> = ({ children }) => {
  // Context dependencies
  const { user, userLicense } = useContext(AuthContext);
  const { humioUrlPrefix, isLoading: tenantLoading } = useTenantInfo();

  // Mutation hooks for terms acceptance
  const [acceptEulaTC] = useAcceptEulaTC();
  const [acceptHumioTC] = useAcceptHumioTC();

  // Compute user ID from auth context
  const userId = useMemo(() => getUserId(user), [user]);

  /**
   * Component state for Humio integration
   */
  const [state, setState] = useState<HumioIntegrationState>(INITIAL_STATE);

  // Token management refs
  const refreshInFlightRef = useRef<Promise<string | undefined> | null>(null);
  const lastRefreshAtRef = useRef<number>(0);
  const tokenCreatedAtRef = useRef<number>(0);
  const acceptHumioInFlightRef = useRef<Promise<AcceptTermsResult> | null>(
    null,
  );
  const dashboardRefetchRef = useRef<DashboardRefetchFunction>();
  const retryCountRef = useRef<number>(0);

  // Timer refs
  const sessionRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  /**
   * Gets the current token age in milliseconds
   * Uses lastUserActivityTimestamp if available (Humio's dashboard tracking),
   * otherwise falls back to our client-side tracking
   */
  const getTokenAge = useCallback((): number => {
    // Priority 1: Check lastUserActivityTimestamp (Humio's dashboard tracking)
    const lastActivity = hasStorage
      ? localStorage.getItem('lastUserActivityTimestamp')
      : null;

    if (lastActivity) {
      const activityTimestamp = parseInt(lastActivity);
      if (!isNaN(activityTimestamp)) {
        const age = Date.now() - activityTimestamp;
        return age;
      }
    }

    // Priority 2: Fallback to our client-side tracking
    if (!tokenCreatedAtRef.current) {
      return Infinity;
    }

    const age = Date.now() - tokenCreatedAtRef.current;
    return age;
  }, []);

  /**
   * Syncs token from localStorage if it differs from state
   * Also syncs tokenCreatedAtRef with lastUserActivityTimestamp if available
   */
  const syncTokenFromStorage = useCallback((): string | undefined => {
    if (!hasStorage || !userId) {
      return state.token;
    }

    const storedToken = localStorage.getItem(
      getUserScopedKey('session_id_token', userId),
    );
    const storedCreatedAt = localStorage.getItem(
      getUserScopedKey('session_id_token_created_at', userId),
    );

    // Check for lastUserActivityTimestamp
    const lastActivity = localStorage.getItem('lastUserActivityTimestamp');

    // Sync tokenCreatedAtRef with the most accurate timestamp
    if (lastActivity) {
      const activityTimestamp = parseInt(lastActivity);
      if (
        !isNaN(activityTimestamp) &&
        activityTimestamp !== tokenCreatedAtRef.current
      ) {
        tokenCreatedAtRef.current = activityTimestamp;
      }
    } else if (storedCreatedAt) {
      const createdAt = parseInt(storedCreatedAt);
      if (!isNaN(createdAt) && createdAt !== tokenCreatedAtRef.current) {
        tokenCreatedAtRef.current = createdAt;
      }
    }

    // Always return the localStorage token if valid
    if (storedToken && isValidToken(storedToken)) {
      // Update state if different (for consistency)
      if (storedToken !== state.token) {
        setState((prev) => ({ ...prev, token: storedToken }));
      }

      return storedToken;
    }

    return state.token;
  }, [userId, state.token]);

  /**
   * Clears the current token and all associated data
   */
  const clearToken = useCallback(() => {
    setState((prev) => ({ ...prev, token: undefined }));

    // Clear localStorage
    if (hasStorage && userId) {
      localStorage.removeItem(getUserScopedKey('session_id_token', userId));
      localStorage.removeItem(
        getUserScopedKey('session_id_token_created_at', userId),
      );
    }

    // Clear caches
    if (userId) {
      clearTokenFromCache(`${userId}:${humioUrlPrefix}`);
    }

    // Reset refs
    tokenCreatedAtRef.current = 0;
    lastRefreshAtRef.current = 0;

    // Clear timers
    if (sessionRefreshTimeoutRef.current) {
      clearTimeout(sessionRefreshTimeoutRef.current);
      sessionRefreshTimeoutRef.current = undefined;
    }
  }, [userId, humioUrlPrefix]);

  /**
   * Refreshes the Humio API session token
   * Refreshes proactively at 22 hours to prevent expiration
   */
  const refreshToken = useCallback(
    async (forceRefresh = false): Promise<string | undefined> => {
      // Validate prerequisites
      if (!userId || tenantLoading || !userLicense?.tenant_type) {
        return undefined;
      }

      const cacheKey = `${userId}:${humioUrlPrefix}`;

      // Rate limiting check
      if (checkRateLimit(cacheKey)) {
        error('Rate limit exceeded for token refresh');
        setState((prev) => ({
          ...prev,
          tokenError: new Error('Too many token refresh attempts'),
          tokenLoading: false,
        }));
        return undefined;
      }

      // Reuse in flight refresh to prevent duplicates
      if (!forceRefresh && refreshInFlightRef.current) {
        return refreshInFlightRef.current;
      }

      // Check if refresh is needed
      const needsRefresh = needsTokenRefresh(
        state.token,
        tokenCreatedAtRef.current,
        forceRefresh,
      );

      if (!needsRefresh) {
        // Check cache for valid token
        const cached = getCachedToken(cacheKey);
        if (cached) {
          return cached;
        }
      }

      // Prevent rapid refreshes
      if (shouldThrottleRefresh(lastRefreshAtRef.current)) {
        const cachedToken = getCachedToken(cacheKey);
        if (cachedToken) {
          return cachedToken;
        }
      }

      // Record refresh attempt
      recordRefreshAttempt(cacheKey);

      // Execute refresh
      const refreshPromise = (async (): Promise<string | undefined> => {
        setState((prev) => ({
          ...prev,
          tokenLoading: true,
          tokenError: undefined,
        }));

        try {
          const idToken = await getIdTokenSafely();
          if (!idToken) {
            throw new Error('No ID token available from Cognito');
          }

          const newToken = await setHumioSessionId({
            idToken,
            humioUrlPrefix,
            forceRefresh: true,
            userId,
          });

          if (!isValidToken(newToken)) {
            throw new Error('Received invalid Humio session token');
          }

          // Only update if we have a valid token
          if (newToken) {
            // Update state and cache
            const timestamp = Date.now();
            tokenCreatedAtRef.current = timestamp;
            lastRefreshAtRef.current = timestamp;

            updateTokenCache(cacheKey, newToken, timestamp);

            // Persist to localStorage
            if (hasStorage) {
              localStorage.setItem(
                getUserScopedKey('session_id_token', userId),
                newToken,
              );
              localStorage.setItem(
                getUserScopedKey('session_id_token_created_at', userId),
                String(timestamp),
              );
            }

            setState((prev) => ({
              ...prev,
              token: newToken,
              tokenLoading: false,
            }));

            // Reset rate limiting on success
            refreshAttempts.set(cacheKey, []);

            // Schedule next proactive refresh at 22 hours
            if (sessionRefreshTimeoutRef.current) {
              clearTimeout(sessionRefreshTimeoutRef.current);
            }
            sessionRefreshTimeoutRef.current = setTimeout(() => {
              refreshToken(true);
            }, TOKEN_CONFIG.PROACTIVE_REFRESH_MS);

            return newToken;
          }

          return undefined;
        } catch (err) {
          error('Token refresh failed:', err);
          setState((prev) => ({
            ...prev,
            tokenError: err as Error,
            tokenLoading: false,
          }));
          throw err;
        } finally {
          refreshInFlightRef.current = null;
        }
      })();

      refreshInFlightRef.current = refreshPromise;
      return refreshPromise;
    },
    [
      userId,
      humioUrlPrefix,
      tenantLoading,
      userLicense?.tenant_type,
      state.token,
    ],
  );

  /**
   * Accepts pending terms (EULA and/or Humio) if needed
   */
  const acceptTermsIfNeeded = useCallback(
    async (options?: AcceptTermsOptions): Promise<AcceptTermsResult> => {
      // Reuse in flight Humio acceptance
      if (acceptHumioInFlightRef.current && options?.acceptHumioOnly) {
        return acceptHumioInFlightRef.current;
      }

      if (state.termsProcessing && options?.acceptHumioOnly) {
        return { success: false, skipped: true };
      }

      setState((prev) => ({ ...prev, termsProcessing: true }));

      try {
        const confirmedKey = getUserScopedKey(
          'humio_terms_accepted_confirmed',
          userId,
        );
        const hasConfirmedAcceptance =
          hasStorage && localStorage.getItem(confirmedKey) === 'true';

        const pendingEula =
          hasStorage &&
          localStorage.getItem(
            getUserScopedKey('accept_privacy_policy', userId),
          ) === 'true';
        const pendingHumio =
          hasStorage &&
          localStorage.getItem(
            getUserScopedKey('humio_tc_accepted', userId),
          ) === 'true';

        // Handle EULA-only acceptance
        if (options?.acceptEulaOnly) {
          if (!pendingEula && state.eulaAccepted) {
            setState((prev) => ({ ...prev, termsProcessing: false }));
            return { success: true, eulaAccepted: true };
          }

          await acceptEulaTC({ errorPolicy: 'all' });
          if (hasStorage) {
            localStorage.removeItem(
              getUserScopedKey('accept_privacy_policy', userId),
            );
          }

          setState((prev) => ({
            ...prev,
            eulaAccepted: true,
            termsProcessing: false,
          }));

          return { success: true, eulaAccepted: true };
        }

        // Handle Humio only acceptance
        if (options?.acceptHumioOnly) {
          if (hasConfirmedAcceptance || state.humioAccepted) {
            if (!state.humioAccepted) {
              setState((prev) => ({
                ...prev,
                humioAccepted: true,
                termsProcessing: false,
              }));
            } else {
              setState((prev) => ({ ...prev, termsProcessing: false }));
            }
            return { success: true, humioAccepted: true, skipped: true };
          }

          const acceptPromise = (async (): Promise<AcceptTermsResult> => {
            const token =
              options?.sessionToken ||
              state.token ||
              (await refreshToken(false));

            if (!token) {
              throw new Error('Could not obtain valid session token');
            }

            const result = await acceptHumioTC({
              variables: { humioSessionToken: token },
              errorPolicy: 'all',
            });

            const body = (result?.data as AcceptHumioResponse)?.acceptHumioTC
              ?.body;
            if (body?.success === false) {
              throw new Error(`Humio TC acceptance failed: ${body?.message}`);
            }

            if (hasStorage) {
              localStorage.removeItem(
                getUserScopedKey('humio_tc_accepted', userId),
              );
              localStorage.setItem(confirmedKey, 'true');
            }

            setState((prev) => ({
              ...prev,
              humioAccepted: true,
              termsProcessing: false,
            }));

            return { success: true, humioAccepted: true, sessionToken: token };
          })();

          acceptHumioInFlightRef.current = acceptPromise;

          try {
            return await acceptPromise;
          } finally {
            acceptHumioInFlightRef.current = null;
          }
        }

        // Full acceptance flow (both EULA and Humio)
        if (!pendingEula && !pendingHumio && hasConfirmedAcceptance) {
          setState((prev) => ({
            ...prev,
            eulaAccepted: true,
            humioAccepted: true,
            termsProcessing: false,
          }));
          return { success: true, skipped: true };
        }

        let eulaAccepted = state.eulaAccepted;
        let humioAccepted = state.humioAccepted;

        // Accept EULA if pending
        if (pendingEula) {
          await acceptEulaTC({ errorPolicy: 'all' });
          if (hasStorage) {
            localStorage.removeItem(
              getUserScopedKey('accept_privacy_policy', userId),
            );
          }
          eulaAccepted = true;
        }

        // Accept Humio terms if pending
        if (pendingHumio || !hasConfirmedAcceptance) {
          const token = state.token || (await refreshToken(false));
          if (!token) {
            throw new Error('Could not obtain valid session token');
          }

          const result = await acceptHumioTC({
            variables: { humioSessionToken: token },
            errorPolicy: 'all',
          });

          const body = (result?.data as AcceptHumioResponse)?.acceptHumioTC
            ?.body;
          if (body?.success === false) {
            throw new Error(`Humio TC acceptance failed: ${body?.message}`);
          }

          if (hasStorage) {
            localStorage.removeItem(
              getUserScopedKey('humio_tc_accepted', userId),
            );
            localStorage.setItem(confirmedKey, 'true');
          }
          humioAccepted = true;
        }

        setState((prev) => ({
          ...prev,
          eulaAccepted,
          humioAccepted,
          termsProcessing: false,
        }));

        return { success: true, eulaAccepted, humioAccepted };
      } catch (err) {
        error('Terms acceptance failed:', err);
        setState((prev) => ({ ...prev, termsProcessing: false }));
        return { success: false, error: err };
      }
    },
    [
      state.termsProcessing,
      state.token,
      state.eulaAccepted,
      state.humioAccepted,
      userId,
      acceptEulaTC,
      acceptHumioTC,
      refreshToken,
    ],
  );

  /**
   * Force refresh dashboards with fresh token
   */
  const forceRefreshDashboards = useCallback(async () => {
    if (!userId) return;

    setState((prev) => ({ ...prev, dashboardsLoading: true }));

    try {
      const freshToken = await refreshToken(true);

      if (freshToken && dashboardRefetchRef.current) {
        const result = await dashboardRefetchRef.current({
          humioSessionToken: freshToken,
        });

        if (result?.data) {
          setState((prev) => ({
            ...prev,
            dashboardsData: result.data,
            dashboardsError: undefined,
          }));
        }
      }
    } catch (err) {
      error('Dashboard refresh failed:', err);
      setState((prev) => ({
        ...prev,
        dashboardsError: err as ApolloError,
      }));
    } finally {
      setState((prev) => ({ ...prev, dashboardsLoading: false }));
    }
  }, [userId, refreshToken]);

  /**
   * Fetch Humio dashboards with automatic retry on authentication failures
   */
  const { loading, refetch } = useGetHumioDashboards({
    skip:
      !state.token ||
      !isValidToken(state.token) ||
      tenantLoading ||
      !state.humioAccepted,
    variables: {
      humioSessionToken: state.token || '',
    },
    onCompleted: (d) => {
      retryCountRef.current = 0;
      setState((prev) => {
        // Only update if dashboardsData has actually changed
        if (JSON.stringify(prev.dashboardsData) === JSON.stringify(d)) {
          return prev;
        }
        return {
          ...prev,
          dashboardsData: d,
          dashboardsError: undefined,
          dashboardsLoading: false,
        };
      });
    },
    onError: async (e) => {
      // Handle authentication errors with automatic retry
      if (isAuthenticationError(e)) {
        if (retryCountRef.current < TOKEN_CONFIG.MAX_DASHBOARD_RETRIES) {
          retryCountRef.current++;

          try {
            // Force refresh to get new token
            const newToken = await refreshToken(true);

            if (newToken) {
              // Small delay before retry
              await new Promise((resolve) => setTimeout(resolve, 500));

              const result = await refetch({
                humioSessionToken: newToken,
              });

              if (result?.data) {
                setState((prev) => ({
                  ...prev,
                  dashboardsData: result.data,
                  dashboardsError: undefined,
                  dashboardsLoading: false,
                }));
              }
            }
          } catch (refreshError) {
            error('Token refresh failed:', refreshError);
            setState((prev) => ({
              ...prev,
              dashboardsError: e,
              dashboardsLoading: false,
            }));
          }
        } else {
          error('Max retry attempts reached');
          setState((prev) => ({
            ...prev,
            dashboardsError: e,
            dashboardsLoading: false,
          }));
        }
        return;
      }

      // Handle downstream service errors
      if (isDownstreamError(e) && dashboardRefetchRef.current) {
        const retryDelay = calculateRetryDelay(retryCountRef.current, true);

        setTimeout(() => {
          dashboardRefetchRef.current?.({
            humioSessionToken: state.token || '',
          });
        }, retryDelay);
        return;
      }

      // Other errors
      error('Dashboard query error:', e);
      setState((prev) => ({
        ...prev,
        dashboardsError: e,
        dashboardsLoading: false,
      }));
    },
  });
  /**
   * Hydrates token from localStorage on component mount
   */
  useEffect(() => {
    if (!hasStorage || !userId || state.token) return;

    const cachedKey = `${userId}:${humioUrlPrefix}`;
    const storedToken = localStorage.getItem(
      getUserScopedKey('session_id_token', userId),
    );
    const createdAtStr = localStorage.getItem(
      getUserScopedKey('session_id_token_created_at', userId),
    );

    if (storedToken && isValidToken(storedToken)) {
      const createdAt = Number(createdAtStr) || Date.now();
      const age = Date.now() - createdAt;

      // Don't hydrate tokens older than 22 hours
      if (age >= TOKEN_CONFIG.PROACTIVE_REFRESH_MS) {
        return;
      }

      tokenCreatedAtRef.current = createdAt;
      updateTokenCache(cachedKey, storedToken, Date.now());
      setState((prev) => ({ ...prev, token: storedToken }));

      // Schedule refresh for remaining time
      const timeUntilRefresh = TOKEN_CONFIG.PROACTIVE_REFRESH_MS - age;
      if (timeUntilRefresh > 0) {
        sessionRefreshTimeoutRef.current = setTimeout(() => {
          refreshToken(true);
        }, timeUntilRefresh);
      }
    }
  }, [userId, humioUrlPrefix, state.token, refreshToken]);

  /**
   * Periodically check token age and refresh automatically at 22 hours
   * Runs every minute to catch tokens approaching expiry
   */
  useEffect(() => {
    if (!state.token || !userId) return;

    const checkInterval = setInterval(() => {
      // Sync timestamps first
      syncTokenFromStorage();

      // Get current token age (uses lastUserActivityTimestamp if available)
      const tokenAge = getTokenAge();

      // Auto-refresh at 22 hours
      if (tokenAge >= TOKEN_CONFIG.PROACTIVE_REFRESH_MS) {
        refreshToken(true);
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(checkInterval);
  }, [state.token, userId, getTokenAge, syncTokenFromStorage, refreshToken]);

  /**
   * Store refetch function for later use
   */
  useEffect(() => {
    dashboardRefetchRef.current = refetch as DashboardRefetchFunction;
  }, [refetch]);

  /**
   * Update loading state
   */
  useEffect(() => {
    setState((prev) => ({ ...prev, dashboardsLoading: loading }));
  }, [loading]);

  /**
   * Auto accept Humio terms when token is ready
   */
  useEffect(() => {
    if (!tenantLoading && userId && state.token && !state.humioAccepted) {
      const confirmed =
        hasStorage &&
        localStorage.getItem(
          getUserScopedKey('humio_terms_accepted_confirmed', userId),
        ) === 'true';

      if (confirmed) {
        setState((prev) => ({ ...prev, humioAccepted: true }));
      } else {
        acceptTermsIfNeeded({
          acceptHumioOnly: true,
          sessionToken: state.token,
        });
      }
    }
  }, [
    tenantLoading,
    userId,
    state.token,
    state.humioAccepted,
    acceptTermsIfNeeded,
  ]);

  /**
   * Handle user change events
   */
  useEffect(() => {
    const handleUserChange = (detail: {
      userId: string;
      previousUserId: string | null;
    }) => {
      const { previousUserId } = detail;

      // Clear previous user's data
      if (previousUserId) {
        clearTokenFromCache(`${previousUserId}:${humioUrlPrefix}`);
      }

      // Reset all state
      acceptHumioInFlightRef.current = null;
      tokenCreatedAtRef.current = 0;
      retryCountRef.current = 0;

      setState(INITIAL_STATE);
    };

    eventBus.on('user-changed', handleUserChange);
    return () => eventBus.off('user-changed', handleUserChange);
  }, [humioUrlPrefix]);

  /**
   * Handle logout events
   */
  useEffect(() => {
    const handleLogout = () => {
      // Clear all caches
      clearAllCaches();

      // Clear in-flight operations
      refreshInFlightRef.current = null;
      acceptHumioInFlightRef.current = null;

      // Reset refs
      tokenCreatedAtRef.current = 0;
      retryCountRef.current = 0;

      // Clear timers
      if (sessionRefreshTimeoutRef.current) {
        clearTimeout(sessionRefreshTimeoutRef.current);
        sessionRefreshTimeoutRef.current = undefined;
      }

      // Reset state
      setState(INITIAL_STATE);
    };

    eventBus.on('user-logout', handleLogout);
    return () => eventBus.off('user-logout', handleLogout);
  }, []);

  /**
   * Initialize token when user is ready
   */
  useEffect(() => {
    if (
      user &&
      userId &&
      userLicense?.tenant_type &&
      !tenantLoading &&
      !state.token
    ) {
      refreshToken(false);
    }
  }, [
    user,
    userId,
    userLicense?.tenant_type,
    tenantLoading,
    state.token,
    refreshToken,
  ]);

  /**
   * Cleanup timers on unmount
   */
  useEffect(() => {
    return () => {
      if (sessionRefreshTimeoutRef.current) {
        clearTimeout(sessionRefreshTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo(
    () => ({
      state,
      refreshToken,
      clearToken,
      acceptTermsIfNeeded,
      forceRefreshDashboards,
      getTokenAge,
      syncTokenFromStorage,
      setLogScaleURL: (url: string | undefined) =>
        setState((prev) => ({ ...prev, logScaleURL: url })),
      setLogScaleDashboardId: (id: string | undefined) =>
        setState((prev) => ({ ...prev, logScaleDashboardId: id })),
      setLogScaleURLParams: (params: string[] | undefined) =>
        setState((prev) => ({ ...prev, logScaleURLParams: params })),
      setIframeKey: (key: number) =>
        setState((prev) => ({ ...prev, iframeKey: key })),
    }),
    [
      state,
      refreshToken,
      clearToken,
      acceptTermsIfNeeded,
      forceRefreshDashboards,
      getTokenAge,
      syncTokenFromStorage,
    ],
  );

  return (
    <HumioIntegrationContext.Provider value={contextValue}>
      {children}
    </HumioIntegrationContext.Provider>
  );
};

/**
 * Hook to access Humio token and related operations
 * @returns Token state and operations
 */
export const useHumioToken = () => {
  const context = useContext(HumioIntegrationContext);
  if (!context) {
    throw new Error(
      'useHumioToken must be used within HumioIntegrationProvider',
    );
  }

  // Always get the raw token from localStorage for session_id_token
  let rawToken: string | undefined = undefined;
  if (typeof window !== 'undefined' && window.localStorage) {
    rawToken = window.localStorage.getItem('session_id_token') || undefined;
  }

  return {
    // Use the raw token if available, otherwise fallback to context.state.token
    token: rawToken || context.state.token,
    isLoading: context.state.tokenLoading,
    error: context.state.tokenError,
    refreshToken: context.refreshToken,
    clearToken: context.clearToken,
  };
};

/**
 * Hook to access LogScale dashboard data and operations
 * @returns Dashboard state and operations
 */
export const useLogScale = () => {
  const context = useContext(HumioIntegrationContext);
  if (!context) {
    throw new Error('useLogScale must be used within HumioIntegrationProvider');
  }
  return {
    logScaleURL: context.state.logScaleURL,
    logScaleDashboardId: context.state.logScaleDashboardId,
    logScaleURLParams: context.state.logScaleURLParams,
    logScaleDashboardsLoading: context.state.dashboardsLoading,
    logScaleDashboardsData: context.state.dashboardsData,
    logScaleDashboardsError: context.state.dashboardsError,
    iframeKey: context.state.iframeKey,
    sessionIdTokenCorelight: context.state.token,
    setLogScaleURL: context.setLogScaleURL,
    setLogScaleDashboardId: context.setLogScaleDashboardId,
    setLogScaleURLParams: context.setLogScaleURLParams,
    setIframeKey: context.setIframeKey,
    forceRefreshDashboards: context.forceRefreshDashboards,
  };
};

/**
 * Hook to manage terms acceptance state
 * @returns Terms acceptance state and operations
 */
export const useTermsAcceptance = () => {
  const context = useContext(HumioIntegrationContext);
  if (!context) {
    throw new Error(
      'useTermsAcceptance must be used within HumioIntegrationProvider',
    );
  }
  return {
    state: {
      eulaAccepted: context.state.eulaAccepted,
      humioAccepted: context.state.humioAccepted,
      isProcessing: context.state.termsProcessing,
      sessionToken: context.state.token,
    },
    acceptTermsIfNeeded: context.acceptTermsIfNeeded,
    resetState: () => {
      warn('resetState is deprecated and has no effect');
    },
  };
};

/**
 * Hook for components that make Humio API calls
 * Ensures token is always fresh before making the call
 *
 * @example
 * ```tsx
 * const { withFreshToken } = useHumioApiCall();
 *
 * const handleQuery = async () => {
 *   const result = await withFreshToken(async (token) => {
 *     return await createQueryJob({
 *       clientSessionId: token,
 *       query: '...'
 *     });
 *   });
 * };
 * ```
 */
export const useHumioApiCall = () => {
  const context = useContext(HumioIntegrationContext);

  if (!context) {
    throw new Error(
      'useHumioApiCall must be used within HumioIntegrationProvider',
    );
  }

  const { state, refreshToken, getTokenAge, syncTokenFromStorage } = context;

  /**
   * Wraps API calls to ensure fresh token
   */
  const withFreshToken = useCallback(
    async <T,>(apiCall: (token: string) => Promise<T>): Promise<T> => {
      // Sync token and timestamps from localStorage
      let token = syncTokenFromStorage();

      // Get token age (automatically uses lastUserActivityTimestamp if available)
      const tokenAge = getTokenAge();

      // Check validity AND age
      if (
        !token ||
        !isValidToken(token) ||
        tokenAge >= TOKEN_CONFIG.PROACTIVE_REFRESH_MS
      ) {
        token = await refreshToken(true);

        if (!token) {
          throw new Error('Failed to obtain valid Humio token');
        }
      }

      try {
        return await apiCall(token);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message.toLowerCase() : '';

        const isAuthError =
          errorMessage.includes('invalid') ||
          errorMessage.includes('session') ||
          errorMessage.includes('unauthorized') ||
          errorMessage.includes('internal server error');

        if (isAuthError) {
          const freshToken = await refreshToken(true);

          if (!freshToken) {
            throw new Error('Failed to refresh token after auth error');
          }

          return await apiCall(freshToken);
        }

        throw error;
      }
    },
    [refreshToken, getTokenAge, syncTokenFromStorage],
  );

  return { withFreshToken, token: state.token };
};
